import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Spin, Tag, Typography, message } from 'antd';
import { useParams, useSearchParams } from 'react-router-dom';
import { sowStudioApi } from '../../config/ApiService';

const { Title, Text } = Typography;

type ApprovalRequest = {
  approverName: string;
  approverRole: string;
  requesterName?: string;
  requesterNote?: string | null;
  dealName?: string;
  companyName?: string;
  dealAmount?: string;
  status: string;
  comments?: string | null;
  decisionDate?: string | null;
  flag: { title: string; description?: string; gapDetail?: string };
  activity?: Array<{ eventType: string; actorName?: string; details?: string; createdOn: string }>;
};

const SowApproverView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const requestedDecision = searchParams.get('decision') === 'request_changes' ? 'request_changes' : 'approve';
  const [request, setRequest] = useState<ApprovalRequest | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await sowStudioApi.get<ApprovalRequest>(`/pricing_tool/sow-approval/${token}`);
      setRequest(response.data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'This approval link is invalid or expired.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [token]);

  const decide = async (decision: 'approve' | 'request_changes') => {
    if (!token || (decision === 'request_changes' && !comment.trim())) {
      message.error('Add a comment explaining the requested changes.');
      return;
    }
    setSubmitting(true);
    try {
      const response = await sowStudioApi.post<ApprovalRequest>(`/pricing_tool/sow-approval/${token}`, {
        decision,
        comment: comment.trim() || undefined,
      });
      setRequest(response.data);
      message.success(decision === 'approve' ? 'Sign-off approved.' : 'Changes requested.');
    } catch (err: any) {
      message.error(err?.response?.data?.detail || 'Unable to record the decision.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 48, textAlign: 'center' }}><Spin size="large" /></div>;
  if (error || !request) return <div style={{ padding: 48, maxWidth: 720, margin: '0 auto' }}><Alert type="error" showIcon message="Approval unavailable" description={error} /></div>;

  const decided = request.status !== 'pending';
  return (
    <div style={{ padding: 32, maxWidth: 760, margin: '0 auto' }}>
      <Title level={2}>SOW sign-off request</Title>
      <Card>
        <Text strong>{request.approverName}</Text><br />
        <Text type="secondary">{request.approverRole}</Text>
        <p><Text>Requested by {request.requesterName || 'the SOW requester'}.</Text></p>
        {request.requesterNote && <p><Text type="secondary">Note: {request.requesterNote}</Text></p>}
        <p><Text type="secondary">{[request.dealName, request.companyName, request.dealAmount ? `$${request.dealAmount}` : null].filter(Boolean).join(' · ')}</Text></p>
        <Tag color={decided ? (request.status === 'approved' ? 'green' : 'orange') : 'blue'}>
          {decided ? (request.status === 'approved' ? 'Cleared' : request.status) : 'Needs sign-off'}
        </Tag>
        <Title level={4}>{request.flag.title}</Title>
        <p>{request.flag.gapDetail || request.flag.description}</p>
        <Button href={`/pricing_tool/sow-approval/${token}/draft`} target="_blank">Open draft</Button>
        {decided ? (
          <Alert type={request.status === 'approved' ? 'success' : 'warning'} showIcon message={`You ${request.status === 'approved' ? 'cleared this sign-off' : 'requested changes'}.`} description={request.comments || undefined} />
        ) : (
          <>
            <Input.TextArea rows={4} maxLength={2000} value={comment} onChange={(event) => setComment(event.target.value)} placeholder="Optional comment for approval, required when requesting changes" />
            <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
              <Button type={requestedDecision === 'approve' ? 'primary' : 'default'} loading={submitting} onClick={() => void decide('approve')}>Approve sign-off</Button>
              <Button type={requestedDecision === 'request_changes' ? 'primary' : 'default'} loading={submitting} onClick={() => void decide('request_changes')}>Request changes</Button>
            </div>
          </>
        )}
        <Title level={5} style={{ marginTop: 24 }}>Activity</Title>
        {(request.activity || []).length === 0 ? <Text type="secondary">No activity yet.</Text> : request.activity!.map((entry, index) => (
          <div key={`${entry.createdOn}-${index}`} style={{ marginBottom: 8 }}>
            <Text strong>{entry.eventType}</Text><Text type="secondary"> · {entry.actorName || 'System'}</Text><br />
            {entry.details && <Text>{entry.details}</Text>}
          </div>
        ))}
      </Card>
    </div>
  );
};

export default SowApproverView;
